// Field app for experts: today's route only, then one visit at a time.
import { api, uploadRaw } from "../lib/api";
import { brand } from "../lib/brand";
import { PRIORITIES, SERVICE_CATEGORIES } from "../lib/catalog";
import { fmt, t, tl } from "../lib/i18n";
import { Link, match, route } from "../lib/router";
import { isAdminUser, session } from "../lib/session";
import { h, invalidate, local, resource } from "../lib/ui";
import { AccountPanel } from "../shared/auth";
import {
  act, Async, Button, confirmDialog, Dialog, Field, formData, isBusy, LangSwitch, openDialog, Select, toast, toastError, VisitStatus,
} from "../shared/components";
import { Icon } from "../shared/icons";

function Header(props: { title: string; sub?: string; back?: string; progress?: number }) {
  return (
    <header class="field-top">
      <div class="row between">
        {props.back ? (
          <Link href={props.back} class="row" style="color:inherit;text-decoration:none;gap:4px;font-weight:600"><Icon name="back" />{t("field.route")}</Link>
        ) : (
          <span class="row" style="gap:8px"><img src={brand.current.markUrl} alt="" width="26" height="26" style="border-radius:7px" /><span class="strong">{brand.current.appName}</span></span>
        )}
        <div class="row" style="gap:6px">
          <LangSwitch />
          {isAdminUser() ? <Link href="/admin" class="icon-btn" title={t("customer.toAdmin")}><Icon name="sliders" size={18} /></Link> : null}
          <Link href="/field/account" class="icon-btn" title={t("account.title")}><Icon name="user" size={18} /></Link>
        </div>
      </div>
      <div class="stack tight" style="margin-top:14px;gap:4px">
        <h1>{props.title}</h1>
        {props.sub ? <span class="small" style="opacity:.8">{props.sub}</span> : null}
        {props.progress !== undefined ? <div class="progress" style="margin-top:8px"><i style={{ width: `${Math.round(props.progress * 100)}%` }} /></div> : null}
      </div>
    </header>
  );
}

function Today() {
  const r = resource("field.today", () => api.get("/api/field/today"));
  const first = session.me?.name.split(" ")[0] ?? "";
  return (
    <div class="fieldapp">
      <Header title={t("field.hello", { name: first })} sub={r.data ? `${fmt.date(r.data.date, "long")} · ${t("field.visits", { count: r.data.visits.length })}` : undefined} />
      <main class="field-body">
        {Async(r, (d: any) => d.visits.length ? (
          <div class="route">
            {d.visits.map((v: any) => {
              const state = v.status === "completed" ? "done" : v.status === "in_progress" ? "active" : v.access_revoked ? "blocked" : v.status === "entry_approved" ? "ready" : "";
              return (
                <Link href={`/field/visits/${v.id}`} class={`stop ${state}`}>
                  <span class="t">{fmt.time(v.scheduled_for)}</span>
                  <div class="card">
                    <div class="row between">
                      <span class="strong">{v.address_line1}</span>
                      {v.access_revoked ? <span class="chip critical">{t("field.accessRevoked")}</span> : <VisitStatus status={v.status} />}
                    </div>
                    <span class="small muted">{t(`visitType.${v.type}`)} · {t(`buildingType.${v.building_type}`)} · {v.code}{v.two_experts ? ` · ${t("field.twoExperts")}` : ""}</span>
                    {v.occupancy !== "occupied" ? <span class="xs" style="color:var(--c-accent-text);font-weight:600">{t(`occupancy.${v.occupancy}`)}</span> : null}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div class="panel"><div class="empty"><strong>{t("field.noVisitsTitle")}</strong><span>{t("field.noVisitsBody")}</span></div></div>
        ), 5)}
        <p class="xs muted" style="text-align:center">{t("field.privacy")}</p>
      </main>
    </div>
  );
}

function findingDialog(visitId: string) {
  openDialog((close) => (
    <Dialog title={t("field.finding.title")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("finding", async () => {
          await api.post(`/api/field/visits/${visitId}/findings`, { title: d.title, service: d.service, priority: d.priority, description: d.description });
          close();
        }, t("field.finding.sent"));
      }}>
        <Field label={t("fields.title")} id="f-title"><input class="input" id="f-title" name="title" required /></Field>
        <div class="cols-2">
          <Field label={t("fields.service")} id="f-svc"><Select id="f-svc" name="service" value="plumbing" options={SERVICE_CATEGORIES.map((x) => ({ value: x, label: t(`service.${x}`) }))} /></Field>
          <Field label={t("fields.priority")} id="f-pr"><Select id="f-pr" name="priority" value="P3" options={PRIORITIES.map((x) => ({ value: x, label: `${x} · ${t(`priority.${x}`)}` }))} /></Field>
        </div>
        <Field label={t("fields.description")} id="f-desc"><textarea class="textarea" id="f-desc" name="description" /></Field>
        <div class="row end"><Button kind="secondary" onClick={close}>{t("common.cancel")}</Button><Button type="submit" busy={isBusy("finding")}>{t("field.finding.send")}</Button></div>
      </form>
    </Dialog>
  ));
}

function position(): Promise<{ lat: number; lng: number; accuracy: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });
}

function VisitScreen(props: { id: string }) {
  const r = resource(`field.visit.${props.id}`, () => api.get(`/api/field/visits/${props.id}`));
  const s = local(`screen:fv.${props.id}`, () => ({ upload: -1, summary: "" }));
  const reload = () => { r.reload(); invalidate("field.today"); };
  return Async(r, (d: any) => {
    const { visit, home, items, results, contacts } = d;
    const byItem = new Map(results.map((x: any) => [x.item_id, x]));
    const done = results.length;
    const inProgress = visit.status === "in_progress";
    const completed = visit.status === "completed";

    const setResult = (item: any, result: string) => act(`res.${item.id}`, async () => {
      const prev: any = byItem.get(item.id);
      await api.put(`/api/field/visits/${visit.id}/results/${item.id}`, { result, note: prev?.note ?? null });
      r.reload();
    });
    const setNote = (item: any, note: string) => {
      const prev: any = byItem.get(item.id);
      if (!prev || (prev.note ?? "") === note) return;
      act(`note.${item.id}`, async () => { await api.put(`/api/field/visits/${visit.id}/results/${item.id}`, { result: prev.result, note: note || null }); prev.note = note; });
    };
    const photo = (item: any, file: File) => act(`photo.${item.id}`, async () => {
      const pos = await position();
      const fd = new FormData();
      fd.append("photo", file);
      if (pos) { fd.append("lat", String(pos.lat)); fd.append("lng", String(pos.lng)); fd.append("accuracy", String(pos.accuracy)); }
      await api.upload(`/api/field/visits/${visit.id}/results/${item.id}/photo`, fd);
      r.reload();
    }, t("field.photoSaved"));
    const checkIn = () => act("checkin", async () => {
      const pos = await position();
      await api.post(`/api/field/visits/${visit.id}/check-in`, pos ? { lat: pos.lat, lng: pos.lng } : {});
      reload();
    }, t("field.checkedIn"));
    const checkOut = async () => {
      const missing = items.length - done;
      if (missing > 0 && !(await confirmDialog({ title: t("field.checkoutTitle"), body: t("field.checkoutMissing", { count: missing }), confirm: t("field.checkout") }))) return;
      act("checkout", async () => {
        const res = await api.post(`/api/field/visits/${visit.id}/check-out`, { summary: s.summary || null });
        reload();
        toast(t("field.checkedOut", { outcome: t(`health.${res.outcome}`) }));
      });
    };
    const uploadVideo = (file: File) => {
      s.set({ upload: 0 });
      uploadRaw(`/api/field/visits/${visit.id}/bodycam?start=${encodeURIComponent(visit.checked_in_at ?? "")}&end=${encodeURIComponent(visit.checked_out_at ?? new Date().toISOString())}`,
        file, (f) => s.set({ upload: f }))
        .then(() => { s.set({ upload: -1 }); toast(t("field.videoUploaded")); r.reload(); })
        .catch((e) => { s.set({ upload: -1 }); toastError(e); });
    };

    const canCheckIn = visit.status === "entry_approved" && !home.access_revoked;
    return (
      <div class="fieldapp">
        <Header back="/field" title={home.address_line1} sub={`${t(`visitType.${visit.type}`)} · ${fmt.time(visit.scheduled_for)} · ${home.code}`}
          progress={inProgress || completed ? (items.length ? done / items.length : 0) : undefined} />
        <main class="field-body">
          {home.access_revoked ? <div class="note danger">{t("field.revokedNote")}</div>
            : visit.status === "entry_approved" ? <div class="note">{t("field.entryApproved")}</div>
            : !inProgress && !completed ? <div class="note warn">{t("field.entryNotApproved")}</div> : null}
          {visit.two_experts ? <div class="note small">{t("field.twoExpertsNote")}</div> : null}

          <section class="panel panel-pad stack tight">
            {home.photo_file_id ? <img class="home-photo" src={`/api/files/${home.photo_file_id}`} alt={t("field.homePhoto")} /> : null}
            <div class="row between"><span class="strong">{t(`buildingType.${home.building_type}`)} · {t(`occupancy.${home.occupancy}`)}</span><VisitStatus status={visit.status} /></div>
            {home.notes ? <p class="small" style="white-space:pre-wrap">{home.notes}</p> : null}
            {contacts.length ? (
              <div class="stack tight" style="margin-top:6px">
                {contacts.map((c: any) => (
                  <div class="row between">
                    <span class="small">{c.name} <span class="muted">· {c.roles.map((x: string) => t(`homeRole.${x}`)).join(", ")}</span></span>
                    {c.phone ? <a class="btn secondary small" href={`tel:${c.phone}`}><Icon name="phone" size={16} />{t("field.call")}</a> : null}
                  </div>
                ))}
              </div>
            ) : null}
            <span class="xs muted">{t("field.goldenRule")}</span>
          </section>

          {inProgress || completed ? (
            <div class="stack">
              <div class="row between"><h2 style="font-size:var(--fs-l)">{t("field.checklist")}</h2><span class="small muted tabular">{t("field.progress", { done, total: items.length })}</span></div>
              {items.map((item: any) => {
                const res: any = byItem.get(item.id);
                return (
                  <section class={`check-item ${res ? `set-${res.result}` : ""}`} key={item.id}>
                    <div class="stack tight" style="gap:2px">
                      <span class="level">{t(`checkLevel.${item.level}`)} · {t(`frequency.${item.frequency}`)}</span>
                      <span class="strong">{tl(item.title)}</span>
                    </div>
                    <div class="result-seg" role="group" aria-label={tl(item.title)}>
                      {["ok", "attention", "critical", "na"].map((x) => (
                        <button class={x} aria-pressed={String(res?.result === x)} disabled={!inProgress || isBusy(`res.${item.id}`)} onClick={() => setResult(item, x)}>{t(`result.${x}`)}</button>
                      ))}
                    </div>
                    {res && (inProgress || res.note) ? (
                      <textarea class="textarea" style="min-height:56px" placeholder={t("field.notePlaceholder")} value={res.note ?? ""} disabled={!inProgress}
                        aria-label={t("fields.note")} onChange={(e: Event) => setNote(item, (e.target as HTMLTextAreaElement).value.trim())} />
                    ) : null}
                    {res ? (
                      <div class="row">
                        {res.photo_file_id ? <img class="photo-thumb" src={`/api/files/${res.photo_file_id}`} alt={t("field.photoOf", { item: tl(item.title) })} /> : null}
                        {inProgress ? (
                          <label class="btn secondary small" style="cursor:pointer">
                            {isBusy(`photo.${item.id}`) ? <span class="spin" /> : <Icon name="camera" size={16} />}
                            {res.photo_file_id ? t("field.retakePhoto") : t("field.addPhoto")}
                            <input class="sr-only" type="file" accept="image/*" capture="environment"
                              onChange={(e: Event) => { const f = (e.target as HTMLInputElement).files?.[0]; (e.target as HTMLInputElement).value = ""; if (f) photo(item, f); }} />
                          </label>
                        ) : null}
                      </div>
                    ) : null}
                  </section>
                );
              })}

              <section class="panel panel-pad stack">
                <div class="row between"><span class="strong">{t("field.bodycam")}</span><span class="small muted">{t("field.videosUploaded", { count: d.videos })}</span></div>
                <span class="small muted">{t("field.bodycamHelp", { days: d.retentionDays })}</span>
                {s.upload >= 0 ? (
                  <div class="stack tight"><div class="upload-bar"><i style={{ width: `${Math.round(s.upload * 100)}%` }} /></div><span class="xs muted tabular">{Math.round(s.upload * 100)}%</span></div>
                ) : (
                  <label class="btn secondary" style="cursor:pointer">
                    <Icon name="upload" size={18} />{t("field.uploadVideo")}
                    <input class="sr-only" type="file" accept="video/mp4,video/webm,video/quicktime"
                      onChange={(e: Event) => { const f = (e.target as HTMLInputElement).files?.[0]; (e.target as HTMLInputElement).value = ""; if (f) uploadVideo(f); }} />
                  </label>
                )}
              </section>

              {inProgress ? (
                <section class="panel panel-pad stack">
                  <Field label={t("field.summary")} id="sum" hint={t("field.summaryHint")}>
                    <textarea class="textarea" id="sum" value={s.summary} onInput={(e: Event) => (s.summary = (e.target as HTMLTextAreaElement).value)} />
                  </Field>
                  <Button kind="secondary" icon="alert" onClick={() => findingDialog(visit.id)}>{t("field.reportFinding")}</Button>
                </section>
              ) : (
                <section class="panel panel-pad stack tight">
                  <span class="strong">{t("field.completedAt", { time: fmt.time(visit.checked_out_at) })}</span>
                  {visit.summary ? <p class="small" style="white-space:pre-wrap">{visit.summary}</p> : null}
                  <Button kind="secondary" size="small" icon="alert" onClick={() => findingDialog(visit.id)}>{t("field.reportFinding")}</Button>
                </section>
              )}
            </div>
          ) : null}
        </main>
        {!completed ? (
          <div class="actionbar">
            <div>
              {inProgress ? (
                <Button size="large" block icon="check" busy={isBusy("checkout")} onClick={checkOut}>{t("field.checkout")}</Button>
              ) : (
                <Button size="large" block icon="door" disabled={!canCheckIn} busy={isBusy("checkin")} onClick={checkIn}>{t("field.checkin")}</Button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }, 6);
}

export function FieldApp() {
  let m;
  if ((m = match("/field/visits/:id"))) return <VisitScreen id={m.id} />;
  if (route.path === "/field/account") {
    return (
      <div class="fieldapp">
        <Header back="/field" title={t("account.title")} />
        <main class="field-body"><AccountPanel /></main>
      </div>
    );
  }
  return <Today />;
}
